import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Send, Edit3, CheckCircle2, Phone, ChevronDown } from 'lucide-react';
import { categories } from '../data/products';
import { useT } from '../utils/i18n';
// SMS-first ordering for contractors. The killer differentiator vs. national
// marketplaces: a contractor on a jobsite can't fill out forms on their
// phone. They text. This component lets them tap a few buttons, opens
// their native Messages app with a perfectly-formatted order pre-filled,
// and they hit send.
//
// Cross-platform sms: link format — using `?&body=` is the most portable
// across iOS Safari / Android Chrome. Number is set to the dispatch line.
const DISPATCH_NUMBER = '+12066193742';
// Build the flat list of materials for the dropdown.
const ALL_MATERIALS = categories.flatMap(c => c.products.map(p => p.name));
const YARD_OPTIONS = [5, 7, 10, 12, 15, 20, 24, 36];
export function SMSOrderCTA() {
  const t = useT();
  const [isOpen, setIsOpen] = useState(false);
  const [material, setMaterial] = useState('5/8" Minus');
  const [yards, setYards] = useState(12);
  const [address, setAddress] = useState('');
  const [timing, setTiming] = useState('asap');
  const [notes, setNotes] = useState('');
  const PRESETS = [{
    label: '12 yd 5/8 Minus',
    material: '5/8" Minus',
    yards: 12
  }, {
    label: '12 yd 1.25 Minus',
    material: '1.25" Minus',
    yards: 12
  }, {
    label: '12 yd 5/8 Clean',
    material: '5/8" Clean',
    yards: 12
  }, {
    label: '12 yd 1.5" Drain Rock',
    material: '1.5" Drain Rock',
    yards: 12
  }, {
    label: '15 yd Topsoil',
    material: '3-Way Topsoil',
    yards: 15
  }];
  const TIMING_OPTIONS = [{
    value: 'asap',
    label: t('sms.timing.asap')
  }, {
    value: 'today-am',
    label: t('sms.timing.todayAm')
  }, {
    value: 'today-pm',
    label: t('sms.timing.todayPm')
  }, {
    value: 'tomorrow-am',
    label: t('sms.timing.tomorrowAm')
  }, {
    value: 'tomorrow-pm',
    label: t('sms.timing.tomorrowPm')
  }, {
    value: 'this-week',
    label: t('sms.timing.thisWeek')
  }];
  const message = useMemo(() => {
    const timingLabel = TIMING_OPTIONS.find(opt => opt.value === timing)?.label || 'ASAP';
    const lines = [`253Gravel order:`, `${yards} yd ${material}`, address ? `Drop: ${address}` : 'Drop: [address]', `When: ${timingLabel}`];
    if (notes.trim()) lines.push(`Notes: ${notes.trim()}`);
    return lines.join('\n');
  }, [material, yards, address, timing, notes, TIMING_OPTIONS]);
  const smsHref = `sms:${DISPATCH_NUMBER}?&body=${encodeURIComponent(message)}`;
  const applyPreset = (preset: (typeof PRESETS)[number]) => {
    setMaterial(preset.material);
    setYards(preset.yards);
  };
  return <section className="bg-stone-900 dark:bg-[#1f1f1f] relative z-10 border-y border-[#fbbf24]/20" data-id="element-1488">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1489">
        {/* Always-visible pull-down header */}
        <button type="button" onClick={() => setIsOpen(v => !v)} aria-expanded={isOpen} aria-controls="sms-order-body" className="w-full flex items-center gap-4 sm:gap-6 py-5 sm:py-6 text-left group" data-id="element-1490">
          <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#fbbf24]/15 border border-[#fbbf24]/30 flex items-center justify-center shrink-0" data-id="element-1491">
            <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-[#fbbf24]" data-id="element-1492" />
          </div>
          <div className="min-w-0 flex-1" data-id="element-1493">
            <div className="inline-flex items-center gap-2 mb-1" data-id="element-1494">
              <span className="w-1.5 h-1.5 rounded-full bg-[#fbbf24] animate-pulse" data-id="element-1495" />
              <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#fbbf24]" data-id="element-1496">
                {t('sms.header.eyebrow')}
              </span>
            </div>
            <p className="font-heading text-lg sm:text-2xl font-extrabold text-white tracking-tight leading-tight" data-id="element-1497">
              {t('sms.header.title1')}
              <span className="text-[#fbbf24]" data-id="element-1498">{t('sms.header.title2')}</span>
            </p>
            <p className="hidden sm:block text-xs text-stone-400 mt-1" data-id="element-1499">
              {isOpen ? t('sms.header.subtitleOpen') : t('sms.header.subtitleClosed')}
            </p>
          </div>
          <motion.div animate={{
          rotate: isOpen ? 180 : 0
        }} transition={{
          duration: 0.25,
          ease: 'easeInOut'
        }} className="shrink-0 w-9 h-9 rounded-full border border-stone-700 group-hover:border-[#fbbf24]/50 flex items-center justify-center text-stone-300 group-hover:text-[#fbbf24] transition-colors" data-id="element-1500">
            <ChevronDown className="w-5 h-5" data-id="element-1501" />
          </motion.div>
        </button>

        {/* Collapsible body */}
        <AnimatePresence initial={false} data-id="element-1502">
          {isOpen && <motion.div key="sms-body" id="sms-order-body" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.35,
          ease: [0.21, 0.47, 0.32, 0.98]
        }} className="overflow-hidden" data-id="element-1503">
              <div className="pt-2 pb-16 lg:pb-20" data-id="element-1504">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-id="element-1505">
                  {/* Pitch */}
                  <motion.div initial={{
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.4,
                delay: 0.05
              }} className="lg:col-span-5" data-id="element-1506">
                    <h3 className="font-heading text-3xl md:text-4xl font-extrabold text-white mb-5 leading-[1.1] tracking-tight" data-id="element-1507">
                      {t('sms.pitch.title1')} <br data-id="element-1508" />
                      <span className="text-[#fbbf24]" data-id="element-1509">
                        {t('sms.pitch.title2')}
                      </span>
                    </h3>
                    <p className="text-stone-300 text-base md:text-lg leading-relaxed mb-7 max-w-md" data-id="element-1510">
                      {t('sms.pitch.desc')}
                    </p>
                    <div className="space-y-3 max-w-md" data-id="element-1511">
                      {[t('sms.pitch.bullet1'), t('sms.pitch.bullet2'), t('sms.pitch.bullet3')].map(line => <div key={line} className="flex items-start gap-3" data-id="element-1512">
                          <CheckCircle2 className="w-5 h-5 text-[#fbbf24] shrink-0 mt-0.5" data-id="element-1513" />
                          <span className="text-stone-200 text-sm" data-id="element-1514">{line}</span>
                        </div>)}
                    </div>
                    <div className="mt-7 flex items-center gap-3 text-sm text-stone-400" data-id="element-1515">
                      <Phone className="w-4 h-4" data-id="element-1516" />
                      <span data-id="element-1517">
                        {t('sms.pitch.call')}{' '}
                        <a href={`tel:${DISPATCH_NUMBER}`} className="text-white font-semibold hover:underline" data-id="element-1518">
                          (206) 619-3742
                        </a>
                      </span>
                    </div>
                  </motion.div>

                  {/* Builder card — looks like a phone */}
                  <motion.div initial={{
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.4,
                delay: 0.1
              }} className="lg:col-span-7" data-id="element-1519">
                    <div className="bg-[#2a2a2a] rounded-2xl border border-stone-700 shadow-2xl overflow-hidden" data-id="element-1520">
                      {/* Header */}
                      <div className="flex items-center justify-between px-5 py-4 bg-[#333] border-b border-stone-700" data-id="element-1521">
                        <div className="flex items-center gap-3" data-id="element-1522">
                          <div className="w-10 h-10 rounded-full bg-[#fbbf24]/15 flex items-center justify-center" data-id="element-1523">
                            <MessageSquare className="w-5 h-5 text-[#fbbf24]" data-id="element-1524" />
                          </div>
                          <div data-id="element-1525">
                            <p className="text-white font-bold text-sm" data-id="element-1526">
                              {t('sms.builder.dispatch')}
                            </p>
                            <p className="text-stone-400 text-[11px]" data-id="element-1527">
                              {t('sms.builder.replies')}
                            </p>
                          </div>
                        </div>
                        <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-bold text-stone-300 bg-stone-700/50 px-2 py-1 rounded" data-id="element-1528">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#1DB954] animate-pulse" data-id="element-1529" />
                          {t('sms.builder.online')}
                        </span>
                      </div>

                      {/* Builder */}
                      <div className="p-5 sm:p-6 space-y-5" data-id="element-1530">
                        {/* Presets */}
                        <div data-id="element-1531">
                          <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400 mb-2" data-id="element-1532">
                            {t('sms.builder.quickOrders')}
                          </label>
                          <div className="flex flex-wrap gap-2" data-id="element-1533">
                            {PRESETS.map(preset => {
                          const active = material === preset.material && yards === preset.yards;
                          return <button key={preset.label} type="button" onClick={() => applyPreset(preset)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? 'bg-[#fbbf24] text-stone-900 border-[#fbbf24]' : 'bg-stone-800 text-stone-300 border-stone-700 hover:border-[#fbbf24]/40 hover:text-white'}`} data-id="element-1534">
                                  {preset.label}
                                </button>;
                        })}
                          </div>
                        </div>

                        {/* Custom builder */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1535">
                          <div data-id="element-1536">
                            <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400 mb-1.5" data-id="element-1537">
                              {t('sms.builder.material')}
                            </label>
                            <select value={material} onChange={e => setMaterial(e.target.value)} className="w-full bg-stone-800 border border-stone-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#fbbf24]/50 focus:border-[#fbbf24]/50 transition-all" data-id="element-1538">
                              {ALL_MATERIALS.map(m => <option key={m} value={m} data-id="element-1539">
                                  {m}
                                </option>)}
                            </select>
                          </div>
                          <div data-id="element-1540">
                            <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400 mb-1.5" data-id="element-1541">
                              {t('sms.builder.yards')}
                            </label>
                            <select value={yards} onChange={e => setYards(parseInt(e.target.value))} className="w-full bg-stone-800 border border-stone-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#fbbf24]/50 focus:border-[#fbbf24]/50 transition-all" data-id="element-1542">
                              {YARD_OPTIONS.map(n => <option key={n} value={n} data-id="element-1543">
                                  {n} yd
                                </option>)}
                            </select>
                          </div>
                        </div>

                        <div data-id="element-1544">
                          <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400 mb-1.5" data-id="element-1545">
                            {t('sms.builder.dropAddress')}
                          </label>
                          <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder={t('sms.builder.addressPlaceholder')} className="w-full bg-stone-800 border border-stone-700 text-white placeholder-stone-500 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#fbbf24]/50 focus:border-[#fbbf24]/50 transition-all" data-id="element-1546" />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1547">
                          <div data-id="element-1548">
                            <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400 mb-1.5" data-id="element-1549">
                              {t('sms.builder.when')}
                            </label>
                            <select value={timing} onChange={e => setTiming(e.target.value)} className="w-full bg-stone-800 border border-stone-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#fbbf24]/50 focus:border-[#fbbf24]/50 transition-all" data-id="element-1550">
                              {TIMING_OPTIONS.map(tOpt => <option key={tOpt.value} value={tOpt.value} data-id="element-1551">
                                  {tOpt.label}
                                </option>)}
                            </select>
                          </div>
                          <div data-id="element-1552">
                            <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400 mb-1.5" data-id="element-1553">
                              {t('sms.builder.notes')}
                            </label>
                            <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('sms.builder.notesPlaceholder')} className="w-full bg-stone-800 border border-stone-700 text-white placeholder-stone-500 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#fbbf24]/50 focus:border-[#fbbf24]/50 transition-all" data-id="element-1554" />
                          </div>
                        </div>

                        {/* Preview bubble */}
                        <div className="rounded-xl bg-stone-900/60 border border-stone-700/50 p-4" data-id="element-1555">
                          <div className="flex items-center justify-between mb-2" data-id="element-1556">
                            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400 inline-flex items-center gap-1.5" data-id="element-1557">
                              <Edit3 className="w-3 h-3" data-id="element-1558" />
                              {t('sms.builder.preview')}
                            </span>
                            <span className="text-[10px] text-stone-500" data-id="element-1559">
                              {t('sms.builder.to')} {DISPATCH_NUMBER}
                            </span>
                          </div>
                          <pre className="font-sans text-sm text-stone-100 whitespace-pre-wrap leading-relaxed" data-id="element-1560">
                            {message}
                          </pre>
                        </div>

                        {/* Send */}
                        <a href={smsHref} className="group flex items-center justify-center gap-2 w-full bg-[#fbbf24] hover:bg-[#f59e0b] text-stone-900 font-bold py-4 rounded-xl text-base transition-colors shadow-[0_4px_20px_rgba(251,191,36,0.25)]" data-id="element-1561">
                          <Send className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" data-id="element-1562" />
                          {t('sms.builder.openMessages')}
                        </a>
                        <p className="text-[11px] text-stone-500 text-center" data-id="element-1563">
                          {t('sms.builder.finePrint')}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}